/**
 * Types specific to WireInterfacesStep
 */
import type { SmartAssetData, CompositionLink } from "../../types";

/** Available interface types for wiring */
export const SOURCE_INTERFACES = ["signal_out", "value_out", "charge_out"] as const;
export const TARGET_INTERFACES = ["signal_in", "value_in", "charge_in"] as const;

export type SourceInterface = (typeof SOURCE_INTERFACES)[number];
export type TargetInterface = (typeof TARGET_INTERFACES)[number];

/** Core link detail with computed properties */
export interface CoreLinkDetail {
  link: CompositionLink;
  coreIsSource: boolean;
  externalId: string;
  externalAsset?: SmartAssetData;
  coreInterface: CompositionLink["source_interface"];
  externalInterface: CompositionLink["target_interface"];
  direction: "in" | "out";
}

/** Signal node for canvas rendering */
export interface SignalNode {
  id: string;
  x: number;
  y: number;
  asset?: SmartAssetData;
  direction: "in" | "out";
  label: string;
}

/** Wire/edge for SVG rendering */
export interface WireEdge {
  id: string;
  path: string;
  color: string;
  markerStart?: string;
  markerEnd?: string;
  label: string;
}

/** Port on core asset */
export interface CorePort {
  id: string;
  x: number;
  y: number;
  color: string;
}

/** Activity log item */
export interface ActivityItem {
  id: string;
  time: string;
  message: string;
}

/** Props for WireInterfacesStep */
export interface WireInterfacesStepProps {
  availableAssets: SmartAssetData[];
  selectedAssetIds: string[];
  links: CompositionLink[];
  addLink: (link: Omit<CompositionLink, "id">) => void;
  removeLink: (id: string) => void;
}

/** Collect unique assets from connections */
export const collectUniqueAssets = (connections: CoreLinkDetail[]): SmartAssetData[] => {
  const map = new Map<string, SmartAssetData>();
  connections.forEach((item) => {
    if (item.externalAsset) {
      map.set(item.externalAsset.id, item.externalAsset);
    }
  });
  return [...map.values()];
};
